import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Calendar, Milestone, Eye, EyeOff } from "lucide-react";
import { SyncPill } from "@/components/SyncPill";
import { ActivityBadge } from "@/components/ActivityBadge";
import { StatusChip } from "@/components/StatusChip";
import { HealthMarker } from "@/components/HealthMarker";
import { openClientProjectPreview } from "@/lib/clientPortalPreview";
import { formatShootDates } from "@/utils/formatShootDates";

/** Phase 4 (2026-10, Design System 2.0 visual reskin) — compressed header panel: a small
 * uppercase kicker (category · Client Portal visibility — same two existing fields, just
 * repositioned above the title instead of pill badges beside it), the title, a
 * Client/PM/sync meta line (PM "Not assigned" now gets a real "Assign" link — opens the SAME
 * Edit modal the page's own Edit button already opens, per Nikhil's confirmation; no new
 * inline editor), then a FACT ROW (Status/Health/Next/Shoot/Last Activity) using the shared
 * StatusChip + HealthMarker primitives. HEALTH is still the EXACT same computation as before
 * (overdueTasks>0 || an un-replied client message) — only its rendering moved from a bespoke
 * chip to the shared HealthMarker. All data-fetching below is UNCHANGED from the prior pass —
 * every field reuses an existing endpoint, independently scope-gated, silently omitted (never
 * a broken placeholder) if the caller lacks that scope. */
export const ProjectSnapshotHeader = ({ project: p, client, stages, actions, onAssignPm }) => {
  const { hasScope } = useAuth();
  const [pm, setPm] = useState(null);
  const [overdueTasks, setOverdueTasks] = useState(null);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const currentIdx = (stages || []).findIndex((s) => s.state === "current");
  const nextMilestone = currentIdx >= 0 ? stages[currentIdx + 1]?.label || null : null;
  // Production bug fix (2026-10) — see utils/formatShootDates.js for why `p.shoot_dates` can't
  // be rendered directly: it's an object, not always a string.
  const shootDatesLabel = formatShootDates(p.shoot_dates);
  const canSeePm = hasScope("client_portal.admin");

  useEffect(() => {
    if (hasScope("client_portal.admin")) {
      api.get(`/v1/client-portal/admin/projects/${p.id}/pm`).then((r) => setPm(r.data.pm || null)).catch(() => {});
      api.get(`/v1/client-portal/admin/projects/${p.id}/messages`).then((r) => {
        const items = r.data.items || [];
        setAwaitingReply(items.length > 0 && items[items.length - 1].sender_role === "client");
      }).catch(() => {});
    }
    if (hasScope("tasks.read")) {
      api.get("/v1/tasks/", { params: { assignee: "all", project_id: p.id, status: "Open", date_bucket: "overdue" } })
        .then((r) => setOverdueTasks(r.data.items?.length || 0)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.id]);

  const healthKnown = overdueTasks !== null || hasScope("client_portal.admin");
  const needsAttention = (overdueTasks || 0) > 0 || awaitingReply;

  return (
    <div className="rounded-md border border-hairline bg-paper px-6 py-5" data-testid="project-snapshot-header">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-faint" data-testid="project-snapshot-kicker">
            {p.category && <span>{p.category}</span>}
            {hasScope("client_portal.admin") && client && (
              <>
                {p.category && <span className="text-faint/60">·</span>}
                <span data-testid="project-snapshot-portal-badge" data-enabled={!!client.portal_enabled} className="inline-flex items-center gap-1">
                  {client.portal_enabled ? <Eye className="h-2.5 w-2.5" /> : <EyeOff className="h-2.5 w-2.5" />}
                  {client.portal_enabled ? "Visible in Client Portal" : "Client Portal Off"}
                </span>
              </>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2.5">
            <h1 className="font-editorial text-3xl md:text-[34px] leading-tight text-ink truncate" data-testid="project-snapshot-name">{p.name}</h1>
            {p.archived && <span className="rounded-full bg-tone-red-bg px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-tone-red-text" data-testid="project-archived-badge">Archived</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12.5px] text-ink-soft">
            {client && <span className="font-semibold text-ink" data-testid="project-snapshot-client">Client {client.name}</span>}
            {canSeePm && (
              pm ? (
                <span data-testid="project-snapshot-pm">PM {pm.name}</span>
              ) : (
                <span data-testid="project-snapshot-pm-missing" className="inline-flex items-center gap-1.5">
                  PM Not assigned
                  <button type="button" onClick={onAssignPm} data-testid="project-snapshot-assign-pm-btn" className="font-semibold text-gold-text hover:underline">Assign</button>
                </span>
              )
            )}
            {p.sync?.state && <SyncPill state={p.sync.state} lastSyncedAt={p.sync.last_synced_at} />}
            <ActivityBadge iso={p.last_activity_at} testId="project-activity-badge" />
          </div>
          <div className="mt-3.5 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="project-snapshot-facts">
            <Fact label="Status"><StatusChip status={p.status} testId="project-snapshot-status-chip" /></Fact>
            {healthKnown && (
              <Fact label="Health"><HealthMarker health={needsAttention ? "needs_attention" : "on_track"} testId="project-snapshot-health-chip" /></Fact>
            )}
            <Fact label="Next">
              {nextMilestone ? (
                <span className="inline-flex items-center gap-1 text-[12px] text-ink-soft" data-testid="project-snapshot-next-milestone"><Milestone className="h-3 w-3 text-faint" /> {nextMilestone}</span>
              ) : <span className="text-[12px] text-faint">—</span>}
            </Fact>
            {shootDatesLabel && (
              <Fact label="Shoot"><span className="inline-flex items-center gap-1 text-[12px] text-ink-soft" data-testid="project-snapshot-shoot-dates"><Calendar className="h-3 w-3 text-faint" /> {shootDatesLabel}</span></Fact>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:shrink-0">
          {hasScope("client_portal.admin") && (
            <button
              onClick={async () => { setPreviewing(true); await openClientProjectPreview(p.id, p.name); setPreviewing(false); }}
              disabled={previewing}
              data-testid="project-preview-client-portal-btn"
              title="Opens this project in the real Client Portal, read-only — no client login or invite needed"
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3.5 py-2 text-[12.5px] font-semibold text-ink-soft hover:border-brand disabled:opacity-40 disabled:hover:border-hairline"
            >
              <Eye className="h-3.5 w-3.5" /> {previewing ? "Opening…" : "Preview Client Portal"}
            </button>
          )}
          {actions}
        </div>
      </div>
    </div>
  );
};

const Fact = ({ label, children }) => (
  <div className="flex items-center gap-1.5" data-testid={`project-fact-${label.toLowerCase()}`}>
    <span className="text-[10px] font-semibold uppercase tracking-wide text-faint">{label}</span>
    {children}
  </div>
);
